import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../style/admingazattenotification.css";
import { AbstractAPI } from "../api";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import API from "../service/axios_config";

const AdminGazattenotification = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [allHeadings, setAllHeadings] = useState([]);

  const [formData, setFormData] = useState({
    id: null,
    message: "",
    gadget_type: "pdf",
    file: null,
  });

  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterchange, setFilterChange] = useState(0); // to trigger re-filtering when notifications change
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef();

  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const paginatedNotifications = notifications.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const data = await AbstractAPI.getAllGazatteLinks();

      setNotifications(data);

      // generate unique headings
      const headings = [...new Set(data.map(i => i.message).filter(Boolean))];
      setAllHeadings(headings);

    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  };

  /* ================= SEARCH DROPDOWN ================= */
  const handleHeadingChange = (value) => {
    setFormData({ ...formData, message: value });

    if (!value) {
      setFiltered([]);
      return;
    }

    const matches = allHeadings.filter(h =>
      h.toLowerCase().includes(value.toLowerCase())
    );

    setFiltered(matches);
    setShowDropdown(true);
  };

  const selectHeading = (value) => {
    setFormData({ ...formData, message: value });
    setShowDropdown(false);
  };

  // close dropdown outside click
  useEffect(() => {
    const close = (e) => {
      if (!dropdownRef.current?.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  /* ================= VALIDATION ================= */

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = () => {
    const e = {};

    if (!formData.message.trim())
      e.message = "Heading required";

    if (!formData.file && !formData.file_path && !formData.gadget_type.startsWith("http"))
      e.file = "File is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      let filePath = null;
      if (formData.file) {
        const uploadRes = await AbstractAPI.uploadGazetteFile(formData.file);
        filePath = uploadRes.filepath;
      }

      const payload = {
        message: formData.message,
        gadget_type: formData.gadget_type || "pdf",
        file_path: filePath || formData.file_path, // Keep existing if not updated
      };

      let data;

      if (editing) {
        data = await AbstractAPI.updateGazatteLink(formData.id, payload);
        setNotifications(notifications.map((l) => (l.id === data.id ? data : l)));
      } else {
        data = await AbstractAPI.createGazatteLink(payload);
        setNotifications([data, ...notifications]);
      }

      resetForm();
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  const resetForm = () => {
    setFormData({ id: null, message: "", gadget_type: "pdf", file: null, file_path: null });
    setEditing(false);
    setErrors({});
  };


  const confirmDelete = async () => {
    try {
      await AbstractAPI.deleteGazatteLink(deleteTarget.id);
      setNotifications(notifications.filter((l) => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  return (
    <div className="admin-links-page">

      <div className="page-header">
        <button className="clear-filters-btn" onClick={() => navigate(-1)}>← Back</button>
        <h2>Manage Gazette Notifications</h2>
      </div>

      <form className="adminform" onSubmit={handleSubmit}>

        <div className="form-group" ref={dropdownRef}>
          <label>Heading</label>
          <input
            value={formData.message}
            placeholder="Type to search or create heading..."
            onChange={(e) => handleHeadingChange(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            className={errors.message ? "error-input" : ""}
          />

          {showDropdown && filtered.length > 0 && (
            <div className="custom-dropdown">
              {filtered.map((item, i) => (
                <div
                  key={i}
                  className="dropdown-item"
                  onClick={() => selectHeading(item)}
                >
                  {item}
                </div>
              ))}
            </div>
          )}

          {errors.message && <span className="error">{errors.message}</span>}
        </div>

        {/* Document Type */}
        <div className="form-group">
          <label>Document Type</label>
          <select
            value={formData.gadget_type.startsWith("http") ? "pdf" : formData.gadget_type}
            onChange={(e) => setFormData({ ...formData, gadget_type: e.target.value })}
          >
            <option value="pdf">PDF</option>
            <option value="ppt">PPT</option>
          </select>
        </div>

        {/* FILE UPLOAD */}
        <div className="form-group">
          <label>Gazette Document</label>
          <input
            type="file"
            accept=".pdf,.ppt,.pptx"
            onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
          />
          {formData.file_path && editing && !formData.file && (
            <div style={{ marginTop: "5px", fontSize: "0.9rem" }}>
              Current File: <a href={`${API.defaults.baseURL}/file/get?filepath=${formData.file_path}`} target="_blank" rel="noreferrer">View File</a>
            </div>
          )}
          {errors.file && (
            <span className="error">{errors.file}</span>
          )}
        </div>

        <div className="form-actions">
          <button className="primary-btn">{editing ? "Update" : "Add"}</button>
          {editing && (
            <button type="button" className="secondary-btn" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* TABLE */}
      <div className="table-section">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3>Saved Notifications</h3>
          <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-end" }}>
            <TextField
              placeholder="Search..."
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: 240,
                backgroundColor: "#fff",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Heading</th>
              <th>Gazette URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedNotifications.filter((item) =>
              item.message
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase())
            ).map((item) => (
              <tr key={item.id}>
                <td>{item.message}</td>
                <td>
                  {item.file_path ? (
                    <a href={`${API.defaults.baseURL}/file/get?filepath=${item.file_path}`} target="_blank" rel="noreferrer">Open File</a>
                  ) : (
                    <a href={item.gadget_type} target="_blank" rel="noreferrer">Open URL</a>
                  )}
                </td>
                <td className="actions">
                  <button onClick={() => { setFormData(item); setEditing(true); }}>Edit</button>
                  <button className="danger" onClick={() => setDeleteTarget(item)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <Pagination
          count={Math.ceil(notifications.length / rowsPerPage)}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </div>
      {deleteTarget && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <h4>Delete Notification?</h4>
            <p><strong>{deleteTarget.message}</strong></p>
            <div className="delete-modal-actions">
              <button onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="danger-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminGazattenotification;
